import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ email: "", password: "", username: "", date_of_birth: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.date_of_birth) {
      setError("Date of birth is required.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to Immersive.");
      nav("/discover", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-8">
      <button
        data-testid="register-back-btn"
        onClick={() => nav("/welcome")}
        className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10"
      >
        <ArrowLeft size={18} />
      </button>

      <div className="mt-8">
        <h1 className="font-display text-4xl tracking-tight text-white">Create account</h1>
        <p className="mt-2 text-neutral-400">You must be 18 or older.</p>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <GoogleSignInButton testId="register-google-btn" label="Continue with Google" />
        <div className="flex items-center gap-2 py-1">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-[10px] uppercase tracking-widest text-neutral-500">or with email</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>
        <Field label="Username">
          <input
            data-testid="register-username-input"
            type="text"
            required
            minLength={2}
            maxLength={30}
            value={form.username}
            onChange={change("username")}
            className="input-field"
          />
        </Field>
        <Field label="Email">
          <input
            data-testid="register-email-input"
            type="email"
            required
            value={form.email}
            onChange={change("email")}
            className="input-field"
          />
        </Field>
        <Field label="Password (min 8 chars)">
          <input
            data-testid="register-password-input"
            type="password"
            required
            minLength={8}
            value={form.password}
            onChange={change("password")}
            className="input-field"
          />
        </Field>
        <Field label="Date of birth (must be 18+)">
          <input
            data-testid="register-dob-input"
            type="date"
            required
            value={form.date_of_birth}
            onChange={change("date_of_birth")}
            className="input-field"
          />
        </Field>

        <label className="flex items-start gap-3 pt-1">
          <input type="checkbox" required data-testid="register-consent-checkbox" className="mt-1 accent-violet-500" />
          <span className="text-xs text-neutral-400 leading-relaxed">
            I confirm I am 18+ and consent to mature and explicit content on characters I mark NSFW.
          </span>
        </label>

        {error && (
          <div data-testid="register-error" className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="register-submit-btn"
          className="btn-pill btn-primary w-full py-3.5 disabled:opacity-50"
        >
          {loading ? "Creating…" : "Create account"}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-neutral-500">
        Already have an account?{" "}
        <Link data-testid="register-login-link" to="/login" className="text-violet-400 hover:text-violet-300">
          Sign in
        </Link>
      </p>

      <style>{`
        .input-field {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 14px;
          padding: 0.9rem 1rem;
          color: white;
          font-family: 'Outfit', sans-serif;
          outline: none;
        }
        .input-field:focus { border-color: rgba(139,92,246,0.5); background: rgba(255,255,255,0.06); }
        input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(0.7); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs uppercase tracking-widest text-neutral-500">{label}</span>
      {children}
    </label>
  );
}
